import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { customerAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';
import { ArrowLeft, MapPin, Package, Check, Loader, DoorOpen, CheckCircle } from 'lucide-react';

export default function SendParcel() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState(null);
  const [boxSize, setBoxSize] = useState('S');
  const [pricing, setPricing] = useState({ S: 0.50, M: 0.70, L: 1.00 });
  const [senderFirstName, setSenderFirstName] = useState('');
  const [senderLastName, setSenderLastName] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [recipientFirstName, setRecipientFirstName] = useState('');
  const [recipientLastName, setRecipientLastName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [touched, setTouched] = useState(false);
  const [formError, setFormError] = useState('');
  const [pollStalled, setPollStalled] = useState(false);
  const [lockerLocked, setLockerLocked] = useState(false);

  useEffect(() => {
    if (user) {
      setSenderFirstName(user.first_name || '');
      setSenderLastName(user.last_name || '');
      setSenderPhone(user.phone || '');
      setSenderEmail(user.email || '');
    }
  }, [user]);
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState(null);

  // Step 3: open box
  const [boxOpening, setBoxOpening] = useState(false);
  const [, setBoxOpened] = useState(false);

  // Step 4: waiting for door close
  const [doorClosed, setDoorClosed] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [timedOut, setTimedOut] = useState(false);   // cancelled by the system: door left open
  const [doorTimer, setDoorTimer] = useState(null);  // {open, warn, cancel} seconds while waiting
  const [cancelMin, setCancelMin] = useState(5);     // server's auto-cancel time, minutes
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);
  const activeOrderRef = useRef(null); // replies for any other order are ignored

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, isGeorgian } = useTranslation();

  useEffect(() => {
    customerAPI.pricing().then(r => {
      const p = r.data;
      if (p && typeof p.S === 'number' && typeof p.M === 'number' && typeof p.L === 'number') setPricing(p);
    }).catch(() => {});
    // A delivery in progress survives a page reload / swipe-back
    try {
      const saved = JSON.parse(sessionStorage.getItem('send_active') || 'null');
      if (saved && saved.result && saved.result.order_no) {
        setResult(saved.result);
        setBoxSize(saved.boxSize || 'S');
        setRecipientFirstName(saved.rf || ''); setRecipientLastName(saved.rl || '');
        setRecipientPhone(saved.rp || ''); setRecipientEmail(saved.re || '');
        setStep(4);
        if (saved.cancelled) setCancelled(true);
        startPolling(saved.result.order_no);
      }
    } catch (e) { /* ignore */ }
    customerAPI.locations().then(r => {
      setLocations(r.data);
      setLoading(false);
      // Auto-select device from QR code scan (?device=XXXXX)
      // Pre-select the locker but keep user on Step 1 so they can choose box size
      const deviceParam = searchParams.get('device');
      if (deviceParam && r.data.length > 0) {
        const match = r.data.find(l => l.device_number === deviceParam);
        if (match) {
          setSelected(match);
          setLockerLocked(true);  // Lock locker selection when from QR
          if (match.available_by_size) {
            const firstAvail = ['S', 'M', 'L'].find(s => match.available_by_size[s] > 0);
            if (firstAvail) setBoxSize(firstAvail);
          }
        }
      }
    }).catch(() => setLoading(false));
    return () => { if (pollRef.current) clearTimeout(pollRef.current); activeOrderRef.current = null; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const forgetActive = () => { try { sessionStorage.removeItem('send_active'); } catch (e) { /* ignore */ } };

  // Choosing a locker by hand: keep a box size that locker actually has free
  const chooseLocker = (loc) => {
    setSelected(loc);
    const avail = loc.available_by_size || {};
    if (avail[boxSize] === 0) {
      const first = ['S', 'M', 'L'].find(sz => avail[sz] > 0);
      if (first) setBoxSize(first);
    }
  };

  // Step 2 -> Step 3: Create draft order (no box open yet)
  const cleanPhone = (v) => {
    const d = String(v || '').replace(/\D/g, '');
    return d.length === 12 && d.startsWith('995') ? d.slice(3) : d;
  };
  const phoneOk = (v) => { const d = cleanPhone(v); return d.length === 9 && d.startsWith('5'); };

  const handleSend = async () => {
    setTouched(true);
    setFormError('');
    if (!selected) return;
    const senderName = `${senderFirstName} ${senderLastName}`.trim();
    if (!senderName) {
      setFormError(isGeorgian ? 'თქვენს პროფილში სახელი არ არის მითითებული. შეავსეთ პროფილი და სცადეთ თავიდან.'
                              : 'Your profile has no name. Please complete your profile and try again.');
      return;
    }
    if (!recipientFirstName.trim() || !recipientLastName.trim() || !recipientPhone) {
      setFormError(isGeorgian ? 'გთხოვთ შეავსოთ ყველა სავალდებულო ველი' : 'Please fill all required fields');
      return;
    }
    if (!phoneOk(recipientPhone)) {
      setFormError(isGeorgian ? 'მიმღების ნომერი უნდა იყოს 9 ციფრი და იწყებოდეს 5-ით, მაგ. 599123456'
                              : 'Recipient phone must be 9 digits starting with 5, e.g. 599123456');
      return;
    }
    if (selected.available_by_size && selected.available_by_size[boxSize] === 0) {
      setFormError(isGeorgian ? 'ამ ზომის თავისუფალი ბოქსი აღარ არის — აირჩიეთ სხვა ზომა' : 'No free box of this size — choose another size');
      return;
    }
    setSending(true);
    try {
      const res = await customerAPI.send({
        device_number: selected.device_number,
        box_size: boxSize,
        sender_name: senderName,
        sender_email: senderEmail || null,
        sender_phone: senderPhone || null,
        recipient_name: `${recipientFirstName.trim()} ${recipientLastName.trim()}`,
        recipient_email: recipientEmail.trim() || null,
        recipient_phone: cleanPhone(recipientPhone),
        notes: notes || null,
      });
      setResult(res.data);
      setStep(3);
    } catch (e) {
      setFormError(e.response?.data?.detail || t('errors.serverError'));
    }
    setSending(false);
  };

  // Step 3: Courier clicks "Open Box"
  
  const handleCancelDraft = async () => {
    if (!result?.order_no) return;
    if (!window.confirm(isGeorgian ? 'ნამდვილად გსურთ შეკვეთის გაუქმება?' : 'Are you sure you want to cancel this order?')) return;
    setCancelling(true);
    try {
      await customerAPI.cancelDraft(result.order_no);
      setCancelled(true);
      if (step === 3) {
        // Not opened yet: nothing to close, go straight back to the start
        forgetActive();
        activeOrderRef.current = null;
        setStep(1); setResult(null); setCancelled(false);
      } else {
        try {
          const saved = JSON.parse(sessionStorage.getItem('send_active') || 'null');
          if (saved) sessionStorage.setItem('send_active', JSON.stringify({ ...saved, cancelled: true }));
        } catch (e) { /* ignore */ }
        // Make sure polling is running so we detect when user closes the door
        if (!polling) startPolling(result.order_no);
      }
    } catch (e) {
      alert(e.response?.data?.detail || (isGeorgian ? 'გაუქმება ვერ მოხერხდა' : 'Failed to cancel'));
    }
    setCancelling(false);
  };

  const handleOpenBox = async () => {
    if (!result) return;
    setBoxOpening(true);
    try {
      await customerAPI.store(result.save_code, result.device_number, boxSize);
      setBoxOpened(true);
      setStep(4);
      try {
        sessionStorage.setItem('send_active', JSON.stringify({ result, boxSize, rf: recipientFirstName,
          rl: recipientLastName, rp: cleanPhone(recipientPhone), re: recipientEmail }));
      } catch (e) { /* ignore */ }
      // Start polling for door close
      startPolling(result.order_no);
    } catch (e) {
      alert(e.response?.data?.detail || t('errors.serverError'));
    }
    setBoxOpening(false);
  };

  // Count the open-door timer down every second between server polls.
  useEffect(() => {
    if (!doorTimer || doorClosed || timedOut) return undefined;
    const t = setInterval(() => setDoorTimer(d => (d ? { ...d, open: d.open + 1 } : d)), 1000);
    return () => clearInterval(t);
  }, [doorTimer !== null, doorClosed, timedOut]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 4: Poll to check if door has closed (order confirmed). One request
  // at a time (next one 5 s after the previous answer), for up to 15 minutes.
  const startPolling = (orderNo) => {
    if (!orderNo) return;
    setPolling(true);
    setPollStalled(false);
    let attempts = 0;
    activeOrderRef.current = orderNo;
    if (pollRef.current) clearTimeout(pollRef.current);
    const finish = (closed) => {
      setDoorClosed(closed);
      setPolling(false);
      forgetActive();
    };
    const tick = async () => {
      if (activeOrderRef.current !== orderNo) return;   // user started over meanwhile
      attempts++;
      try {
        const doorCheck = await customerAPI.checkDoorClosed(orderNo);
        if (activeOrderRef.current !== orderNo) return;
        const status = doorCheck.data.status;
        if (status === 'waiting' && doorCheck.data.open_seconds != null) {
          setDoorTimer({ open: doorCheck.data.open_seconds, warn: doorCheck.data.warn_after_seconds,
                         cancel: doorCheck.data.cancel_after_seconds });
          if (doorCheck.data.cancel_after_seconds) setCancelMin(Math.round(doorCheck.data.cancel_after_seconds / 60));
        }
        if (status === 'cancelled' && doorCheck.data.reason === 'door_left_open') {
          // The system cancelled it: the door stayed open too long.
          setTimedOut(true);
          setCancelled(true);
          if (doorCheck.data.actually_closed === true) { finish(true); return; }
        } else if (status === 'confirmed') {
          finish(true);
          return;
        } else if (status === 'cancelled') {
          // Cancelled (by the courier, or released by staff): never show "confirmed"
          setCancelled(true);
          if (doorCheck.data.actually_closed === true) { finish(true); return; }
        } else if (status === 'not_found') {
          // Not this account's order (or gone): start over
          forgetActive();
          activeOrderRef.current = null;
          setPolling(false);
          setStep(1); setResult(null); setCancelled(false); setDoorClosed(false); setDoorTimer(null);
          return;
        }
      } catch (e) { /* network hiccup: keep trying */ }
      if (attempts >= 180) {          // 15 minutes
        setPolling(false);
        setPollStalled(true);
        return;
      }
      pollRef.current = setTimeout(tick, 5000);
    };
    pollRef.current = setTimeout(tick, 3000);
  };

  const sizes = [
    { value: 'S', label: isGeorgian ? 'პატარა' : 'Small', desc: '9 × 35 × 48 ' + (isGeorgian ? 'სმ' : 'cm'), icon: '📦' },
    { value: 'M', label: isGeorgian ? 'საშუალო' : 'Medium', desc: '20 × 35 × 48 ' + (isGeorgian ? 'სმ' : 'cm'), icon: '📦' },
    { value: 'L', label: isGeorgian ? 'დიდი' : 'Large', desc: '28 × 35 × 48 ' + (isGeorgian ? 'სმ' : 'cm'), icon: '📦' },
  ];

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.back} onClick={() => step <= 2 && !result ? (step > 1 ? setStep(step - 1) : navigate('/home')) : (!doorClosed ? null : navigate('/home'))}>
          <ArrowLeft size={18} /> {t('common.back')}
        </div>
        <span style={s.headerTitle}>{t('parcel.sendTitle')}</span>
        <div style={{ width: 60 }} />
      </header>

      {/* Progress */}
      <div style={s.progress}>
        {[1, 2, 3, 4].map(n => (
          <div key={n} style={{ ...s.dot, background: step >= n ? '#8BC63E' : '#252733' }} />
        ))}
      </div>

      <div style={s.content}>

        {/* ========== Step 1: Choose Location ========== */}
        {step === 1 && (
          <div>
            <h2 style={s.stepTitle}>{t('parcel.selectLocation')}</h2>
            <p style={s.stepSub}>{isGeorgian ? 'აირჩიეთ სად გსურთ ამანათის შენახვა' : 'Select where you want to store the parcel'}</p>
            {lockerLocked && (
              <div style={{
                background: 'rgba(139,198,62,0.1)',
                border: '1px solid rgba(139,198,62,0.3)',
                borderRadius: 10,
                padding: '10px 14px',
                fontSize: 12,
                color: '#8BC63E',
                marginBottom: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                🔒 {isGeorgian ? 'ლოკერი არჩეულია QR-დან — აირჩიეთ ბოქსის ზომა' : 'Locker selected from QR — choose box size'}
              </div>
            )}
            {loading ? <div style={s.loadingBox}><Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> {t('common.loading')}</div> : (
              <div style={s.locationList}>
                {(lockerLocked 
                  ? locations.filter(l => l.device_number === selected?.device_number)
                  : locations.filter(l => l.available_boxes > 0)
                ).map(loc => (
                  <div key={loc.device_number}
                    style={{ 
                      ...s.locationCard, 
                      borderColor: selected?.device_number === loc.device_number ? '#8BC63E' : '#252733',
                      cursor: lockerLocked ? 'default' : 'pointer',
                    }}
                    onClick={() => !lockerLocked && chooseLocker(loc)}
                    role="button" tabIndex={0}
                    onKeyDown={e => { if (!lockerLocked && (e.key === 'Enter' || e.key === ' ')) chooseLocker(loc); }}>
                    <div style={s.locIcon}><MapPin size={20} color="#8BC63E" /></div>
                    <div style={s.locInfo}>
                      <div style={s.locName}>{loc.name}</div>
                      <div style={s.locAddr}>{loc.location} {loc.address && `— ${loc.address}`}</div>
                      <div style={s.locAvail}>{loc.available_boxes} {isGeorgian ? 'თავისუფალი ბოქსი' : 'boxes available'}</div>
                    </div>
                    {selected?.device_number === loc.device_number && <Check size={20} color="#8BC63E" />}
                  </div>
                ))}
                {locations.filter(l => l.available_boxes > 0).length === 0 && (
                  <div style={s.empty}>{t('parcel.noAvailableBoxes')}</div>
                )}
              </div>
            )}
            <h3 style={{ ...s.stepTitle, fontSize: 16, marginTop: 28 }}>{t('parcel.boxSize')}</h3>
            <div style={s.sizeGrid}>
              {sizes.map(sz => (
                <div key={sz.value}
                  style={{ ...s.sizeCard, 
                    borderColor: boxSize === sz.value ? '#8BC63E' : '#252733',
                    opacity: (selected?.available_by_size?.[sz.value] === 0) ? 0.35 : 1,
                    pointerEvents: (selected?.available_by_size?.[sz.value] === 0) ? 'none' : 'auto',
                  }}
                  onClick={() => setBoxSize(sz.value)}>
                  <div style={{ fontSize: sz.value === "S" ? 22 : sz.value === "M" ? 32 : 42 }}>{sz.icon}</div>
                  <div style={s.sizeName}>{sz.label}</div>
                  <div style={s.sizeDesc}>{sz.desc}</div>
                </div>
              ))}
            </div>
            <button style={{ ...s.btn, opacity: selected ? 1 : 0.5 }} onClick={() => selected && setStep(2)}>
              {t('common.next')}
            </button>
          </div>
        )}

        {/* ========== Step 2: Recipient Details ========== */}
        {step === 2 && (
          <div>
            <h2 style={s.stepTitle}>{isGeorgian ? 'ამანათის დეტალები' : 'Parcel Details'}</h2>
            <p style={s.stepSub}>{isGeorgian ? 'შეავსეთ ყველა სავალდებულო ველი' : 'Please fill all required fields'}</p>
            
            <div style={s.sectionLabel}>{isGeorgian ? 'გამომგზავნი' : 'Sender'}</div>
            
            <div style={s.senderCard}>
              <div style={s.senderAvatar}>
                {(senderFirstName?.[0] || senderEmail?.[0] || 'U').toUpperCase()}
              </div>
              <div style={{flex: 1, minWidth: 0}}>
                <div style={s.senderName}>
                  {senderFirstName} {senderLastName}
                </div>
                <div style={s.senderMeta}>
                  {senderEmail}{senderPhone ? ` · ${senderPhone}` : ''}
                </div>
              </div>
            </div>
            
            <div style={{...s.sectionLabel, marginTop: 20}}>{isGeorgian ? 'მიმღები' : 'Recipient'}</div>
            
            <div style={s.twoCol}>
              <div style={{flex: 1}}>
                <label style={s.label} htmlFor="rcpt-first">{isGeorgian ? 'სახელი' : 'First Name'} *</label>
                <input id="rcpt-first" name="recipient-first" autoComplete="off" autoCapitalize="words" enterKeyHint="next"
                  style={{...s.input, ...(touched && !recipientFirstName.trim() ? s.inputError : {})}}
                  aria-invalid={touched && !recipientFirstName.trim()}
                  placeholder={isGeorgian ? 'სახელი' : 'First name'}
                  value={recipientFirstName} onChange={e => setRecipientFirstName(e.target.value)} />
              </div>
              <div style={{flex: 1}}>
                <label style={s.label} htmlFor="rcpt-last">{isGeorgian ? 'გვარი' : 'Last Name'} *</label>
                <input id="rcpt-last" name="recipient-last" autoComplete="off" autoCapitalize="words" enterKeyHint="next"
                  style={{...s.input, ...(touched && !recipientLastName.trim() ? s.inputError : {})}}
                  aria-invalid={touched && !recipientLastName.trim()}
                  placeholder={isGeorgian ? 'გვარი' : 'Last name'}
                  value={recipientLastName} onChange={e => setRecipientLastName(e.target.value)} />
              </div>
            </div>
            <label style={s.label} htmlFor="rcpt-phone">{isGeorgian ? 'ტელეფონი' : 'Phone'} *</label>
            <input id="rcpt-phone" name="recipient-phone" autoComplete="off" inputMode="tel" enterKeyHint="next"
              style={{...s.input, ...(touched && !phoneOk(recipientPhone) ? s.inputError : {})}}
              aria-invalid={touched && !phoneOk(recipientPhone)}
              type="tel" placeholder={isGeorgian ? 'მაგ: 599123456 — SMS-ით მიიღებს კოდს' : 'e.g. 599123456 — will receive SMS'}
              value={recipientPhone} onChange={e => setRecipientPhone(e.target.value)} maxLength={20} />
            <label style={s.label} htmlFor="rcpt-email">{isGeorgian ? 'ელ-ფოსტა' : 'Email'} ({isGeorgian ? 'არასავალდებულო' : 'optional'})</label>
            <input id="rcpt-email" name="recipient-email" autoComplete="off" autoCapitalize="none" autoCorrect="off"
              spellCheck={false} inputMode="email" enterKeyHint="next" style={s.input}
              type="email" placeholder={isGeorgian ? 'ელ-ფოსტა (არასავალდებულო)' : 'Email (optional)'}
              value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} />
            
            <label style={s.label} htmlFor="parcel-notes">{t('parcel.description')} ({t('common.optional')})</label>
            <input id="parcel-notes" name="parcel-notes" autoComplete="off" enterKeyHint="done" maxLength={200} style={s.input}
              placeholder={isGeorgian ? 'რას აგზავნით? მაგ: დოკუმენტები' : 'What are you sending? e.g. Documents'}
              value={notes} onChange={e => setNotes(e.target.value)} />
            
            <div style={s.summary}>
              <div style={s.summaryRow}><span>{t('orders.location')}</span><span>{selected?.name}</span></div>
              <div style={s.summaryRow}><span>{t('parcel.boxSize')}</span><span>{sizes.find(x => x.value === boxSize)?.label}</span></div>
              <div style={s.summaryRow}><span>{isGeorgian ? 'ტარიფი' : 'Rate'}</span><span style={{ color: '#ffb020', fontWeight: 700 }}>{(pricing[boxSize] || 0.50).toFixed(2)} ₾/{isGeorgian ? 'სთ' : 'hr'}</span></div>
              <div style={{fontSize: 11, color: '#8789a0', textAlign: 'right', marginTop: 4}}>{isGeorgian ? 'მიმღები იხდის გატანისას' : 'Recipient pays on pickup'}</div>
            </div>
            
            {formError && (
              <div style={s.errorMsg} role="alert">{formError}</div>
            )}
            
            <button style={s.btn} onClick={handleSend} disabled={sending}>
              {sending ? t('common.loading') : t('common.next')}
            </button>
          </div>
        )}

        {/* ========== Step 3: Open Box ========== */}
        {step === 3 && result && (
          <div style={{ textAlign: 'center' }}>
            <div style={s.bigIcon}>
              <DoorOpen size={48} color="#4d90fe" />
            </div>
            <h2 style={s.stepTitle}>{isGeorgian ? 'გახსენით ბოქსი' : 'Open the Box'}</h2>
            <p style={s.stepSub}>
              {isGeorgian
                ? 'დააჭირეთ ღილაკს ბოქსის გასახსნელად. მოათავსეთ ამანათი შიგნით და დახურეთ კარი.'
                : 'Press the button to open the box. Place your parcel inside and close the door.'}
            </p>

            <div style={s.infoCard}>
              <div style={s.infoRow}>
                <span style={s.infoLabel}>{t('orders.location')}</span>
                <span style={s.infoValue}>{result.device_location}</span>
              </div>
              {result.box_name && (
                <div style={s.infoRow}>
                  <span style={s.infoLabel}>{isGeorgian ? 'ბოქსი' : 'Box'}</span>
                  <span style={s.infoValue}>{result.box_name}</span>
                </div>
              )}
              <div style={s.infoRow}>
                <span style={s.infoLabel}>{isGeorgian ? 'შეკვეთა' : 'Order'}</span>
                <span style={s.infoValue}>{result.order_no}</span>
              </div>
            </div>

            <button style={s.openBtn} onClick={handleOpenBox} disabled={boxOpening}>
              {boxOpening ? (
                <><Loader size={20} style={{ animation: 'spin 1s linear infinite' }} /> {isGeorgian ? 'იხსნება...' : 'Opening...'}</>
              ) : (
                <><DoorOpen size={20} /> {isGeorgian ? 'ბოქსის გახსნა' : 'Open Box'}</>
              )}
            </button>
            <button style={{ ...s.openBtn, background: 'transparent', border: '1px solid #252733', color: '#8a8da4', marginTop: 10 }}
              onClick={handleCancelDraft} disabled={cancelling || boxOpening}>
              {cancelling ? (isGeorgian ? 'უქმდება...' : 'Cancelling...') : (isGeorgian ? 'ჯავშნის გაუქმება' : 'Cancel booking')}
            </button>
          </div>
        )}

        {/* ========== Step 4: Waiting for Door Close ========== */}
        {step === 4 && (
          <div style={{ textAlign: 'center' }}>
            {timedOut ? (
              <>
                <div style={{ ...s.bigIcon, background: 'rgba(255,68,102,0.1)' }}>
                  <Package size={48} color="#ff4466" />
                </div>
                <h2 style={s.stepTitle}>{isGeorgian ? 'გაგზავნა გაუქმდა' : 'Delivery cancelled'}</h2>
                <p style={s.stepSub}>
                  {isGeorgian
                    ? `ბოქსის კარი ${cancelMin} წუთზე მეტხანს ღია დარჩა, ამიტომ გაგზავნა გაუქმდა. ამანათი არ არის რეგისტრირებული — ამოიღეთ ის და დახურეთ კარი.`
                    : `The box door stayed open for more than ${cancelMin} minutes, so the delivery was cancelled. Your parcel is NOT registered — take it out and close the door.`}
                </p>
                {!doorClosed && (
                  <div style={{ ...s.waitingCard, borderColor: 'rgba(255,68,102,0.4)', color: '#ff4466' }}>
                    <div style={{ ...s.pulsingDot, background: '#ff4466' }} />
                    <span>{isGeorgian ? 'კარი ჯერ კიდევ ღიაა' : 'The door is still open'}</span>
                  </div>
                )}
                <button style={s.openBtn} onClick={() => {
                  if (pollRef.current) clearTimeout(pollRef.current);
                  activeOrderRef.current = null;
                  forgetActive();
                  setPolling(false);
                  setStep(1); setResult(null); setCancelled(false); setTimedOut(false); setDoorClosed(false); setDoorTimer(null);
                }}>
                  {isGeorgian ? 'თავიდან დაწყება' : 'Start again'}
                </button>
              </>
            ) : cancelled && doorClosed ? (
              <>
                <div style={{ ...s.bigIcon, background: 'rgba(255,176,32,0.1)' }}>
                  <Package size={48} color="#ffb020" />
                </div>
                <h2 style={s.stepTitle}>
                  {isGeorgian ? 'შეკვეთა გაუქმდა' : 'Order Cancelled'}
                </h2>
                <p style={s.stepSub}>
                  {isGeorgian
                    ? 'შეგიძლიათ აირჩიოთ სხვა ზომის ბოქსი ან გააუქმოთ შეკვეთა.'
                    : 'You can choose a different box size or exit.'}
                </p>
                <button style={s.openBtn} onClick={() => {
                  if (pollRef.current) clearTimeout(pollRef.current);
                  activeOrderRef.current = null;
                  forgetActive();
                  setPolling(false);
                  setStep(1);
                  setResult(null);
                  setCancelled(false);
                  setDoorClosed(false);
                  setDoorTimer(null);
                }}>
                  {isGeorgian ? 'სხვა ზომის არჩევა' : 'Choose Different Size'}
                </button>
                <button style={{...s.openBtn, background:'transparent', border:'1px solid #252733', color:'#8789a0', marginTop: 8}} onClick={() => navigate('/home')}>
                  {isGeorgian ? 'მთავარზე დაბრუნება' : 'Back to Home'}
                </button>
              </>
            ) : cancelled && !doorClosed ? (
              <>
                <div style={{ ...s.bigIcon, background: 'rgba(255,176,32,0.1)' }}>
                  <Package size={48} color="#ffb020" />
                </div>
                <h2 style={s.stepTitle}>
                  {isGeorgian ? 'გთხოვთ დახუროთ კარი' : 'Please close the door'}
                </h2>
                <p style={s.stepSub}>
                  {isGeorgian
                    ? 'შეკვეთა გაუქმდა. დახურეთ კარი ცარიელი ბოქსით.'
                    : 'Order is cancelled. Please close the door (empty box).'}
                </p>
                <div style={s.waitingCard}>
                  <div style={s.pulsingDot} />
                  <span>
                    {isGeorgian ? 'ველოდებით კარის დახურვას...' : 'Waiting for door to close...'}
                  </span>
                </div>
              </>
            ) : !doorClosed ? (
              <>
                <div style={{ ...s.bigIcon, background: 'rgba(255,176,32,0.1)' }}>
                  <Package size={48} color="#ffb020" />
                </div>
                <h2 style={s.stepTitle}>{isGeorgian ? 'მოათავსეთ ამანათი' : 'Place Your Parcel'}</h2>
                <p style={s.stepSub}>
                  {isGeorgian
                    ? 'ბოქსი გახსნილია. მოათავსეთ ამანათი შიგნით და დახურეთ კარი.'
                    : 'The box is open. Place your parcel inside and close the door.'}
                </p>

                <div style={s.waitingCard}>
                  <div style={s.pulsingDot} />
                  <span>
                    {isGeorgian
                      ? 'ველოდებით კარის დახურვას...'
                      : 'Waiting for door to close...'}
                  </span>
                </div>
                {doorTimer && doorTimer.open >= doorTimer.warn && (
                  <div style={s.doorCountdown} role="alert">
                    {(() => {
                      const left = Math.max(0, doorTimer.cancel - doorTimer.open);
                      const mm = Math.floor(left / 60), ss = String(left % 60).padStart(2, '0');
                      return isGeorgian
                        ? `დახურეთ კარი ${mm}:${ss}-ში, თორემ გაგზავნა გაუქმდება.`
                        : `Close the door within ${mm}:${ss} or this delivery will be cancelled.`;
                    })()}
                  </div>
                )}

                {pollStalled && (
                  <div style={{ ...s.errorMsg, marginTop: 16 }} role="alert">
                    {isGeorgian ? 'დადასტურება ჯერ ვერ მივიღეთ. თუ კარი დახურეთ, შეამოწმეთ „ჩემი ამანათები“.' : "We couldn't confirm yet. If you closed the door, check My Parcels."}
                    <button style={{ ...s.openBtn, marginTop: 10 }} onClick={() => startPolling(result?.order_no)}>
                      {isGeorgian ? 'თავიდან შემოწმება' : 'Check again'}
                    </button>
                  </div>
                )}

                <p style={{ fontSize: 13, color: '#8a8da4', marginTop: 24 }}>
                  {isGeorgian
                    ? 'კარის დახურვის შემდეგ შეკვეთა ავტომატურად დადასტურდება და მიმღებს გაეგზავნება კოდი.'
                    : 'Once the door is closed, the order will be automatically confirmed and the recipient will receive the pickup code.'}
                </p>
                <button 
                  onClick={handleCancelDraft} 
                  disabled={cancelling}
                  style={{
                    marginTop: 24,
                    padding: '14px 24px',
                    background: 'transparent',
                    border: '1px solid #ff4466',
                    color: '#ff4466',
                    borderRadius: 10,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    width: '100%',
                  }}>
                  {cancelling 
                    ? (isGeorgian ? 'უქმდება...' : 'Cancelling...') 
                    : (isGeorgian ? 'არ ეტევა? გააუქმე და სცადე სხვა ზომა' : "Doesn't fit? Cancel and try another size")
                  }
                </button>
              </>
            ) : (
              <>
                <div style={{ ...s.bigIcon, background: 'rgba(139,198,62,0.1)' }}>
                  <CheckCircle size={48} color="#8BC63E" />
                </div>
                <h2 style={{ ...s.stepTitle, color: '#8BC63E' }}>
                  {isGeorgian ? 'შეკვეთა დადასტურებულია!' : 'Order Confirmed!'}
                </h2>
                <p style={s.stepSub}>
                  {isGeorgian
                    ? `მიმღებს (${`${recipientFirstName} ${recipientLastName}`}) გაეგზავნა გატანის კოდი SMS-ით${recipientEmail ? ' და ელ-ფოსტით' : ''}.`
                    : `Pickup code has been sent to ${`${recipientFirstName} ${recipientLastName}`} by SMS${recipientEmail ? ' and email' : ''}.`}
                </p>

                <div style={s.confirmedCard}>
                  {recipientEmail && (
                    <div style={s.confirmedRow}>
                      <span>✉️</span>
                      <span>{recipientEmail}</span>
                    </div>
                  )}
                  {recipientPhone && (
                    <div style={s.confirmedRow}>
                      <span>📱</span>
                      <span>{recipientPhone}</span>
                    </div>
                  )}
                </div>

                <div style={s.infoCard}>
                  <div style={s.infoRow}>
                    <span style={s.infoLabel}>{isGeorgian ? 'შეკვეთა' : 'Order'}</span>
                    <span style={s.infoValue}>{result.order_no}</span>
                  </div>
                  <div style={s.infoRow}>
                    <span style={s.infoLabel}>{t('orders.location')}</span>
                    <span style={s.infoValue}>{result.device_location}</span>
                  </div>
                </div>

                <button style={s.btn} onClick={() => navigate('/home')}>
                  {isGeorgian ? 'მთავარ გვერდზე დაბრუნება' : 'Back to Home'}
                </button>
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

const s = {
  doorCountdown: { marginTop: 16, padding: '14px 16px', borderRadius: 12, border: '2px solid #ff4466', background: 'rgba(255,68,102,0.1)', color: '#ff4466', fontSize: 15, fontWeight: 700 },
  page: { minHeight: '100vh', background: '#07080c', color: '#e4e5ed' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid #252733' },
  back: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#8789a0', cursor: 'pointer' },
  headerTitle: { fontSize: 15, fontWeight: 600 },
  progress: { display: 'flex', justifyContent: 'center', gap: 8, padding: '20px 0' },
  dot: { width: 10, height: 10, borderRadius: '50%', transition: 'background 0.3s' },
  content: { maxWidth: 520, margin: '0 auto', padding: '0 16px 40px' },
  stepTitle: { fontSize: 20, fontWeight: 700, marginBottom: 4, textAlign: 'center' },
  stepSub: { fontSize: 14, color: '#8789a0', marginBottom: 24, textAlign: 'center' },
  loadingBox: { display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', padding: 40, color: '#8a8da4' },
  locationList: { display: 'flex', flexDirection: 'column', gap: 10 },
  locationCard: { display: 'flex', alignItems: 'center', gap: 14, padding: 16, background: '#0f1117', border: '1.5px solid #252733', borderRadius: 12, cursor: 'pointer', transition: 'border-color 0.2s' },
  locIcon: { width: 44, height: 44, borderRadius: 10, background: 'rgba(139,198,62,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  locInfo: { flex: 1 },
  locName: { fontSize: 15, fontWeight: 600, marginBottom: 2 },
  locAddr: { fontSize: 12, color: '#8789a0' },
  locAvail: { fontSize: 12, color: '#8BC63E', marginTop: 4, fontWeight: 600 },
  sizeGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 24 },
  sizeCard: { background: '#0f1117', border: '1.5px solid #252733', borderRadius: 12, padding: 16, textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s' },
  sizeName: { fontSize: 14, fontWeight: 700, marginTop: 8 },
  sizeDesc: { fontSize: 11, color: '#8789a0', marginTop: 4 },
  senderCard: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: '#1a1b25', borderRadius: 12, border: '1px solid #252733', marginBottom: 8 },
  senderAvatar: { width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #8BC63E, #4d90fe)', color: '#07080c', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 800, flexShrink: 0 },
  senderName: { fontSize: 14, fontWeight: 600, color: '#e4e5ed', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  senderMeta: { fontSize: 12, color: '#8789a0', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  sectionLabel: { fontSize: 12, fontWeight: 700, color: '#8BC63E', textTransform: 'uppercase', letterSpacing: 1, marginTop: 16, marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #252733' },
  twoCol: { display: 'flex', gap: 10 },
  inputError: { borderColor: '#ff4466', background: 'rgba(255,68,102,0.05)' },
  errorMsg: { background: 'rgba(255,68,102,0.1)', border: '1px solid rgba(255,68,102,0.3)', color: '#ff4466', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginTop: 12, marginBottom: 4 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#8789a0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, marginTop: 16 },
  input: { width: '100%', background: '#161821', border: '1px solid #5c6078', borderRadius: 10, padding: '13px 14px', fontSize: 16, color: '#e4e5ed', outline: 'none', boxSizing: 'border-box' },
  summary: { background: '#0f1117', border: '1px solid #252733', borderRadius: 12, padding: 16, margin: '24px 0' },
  summaryRow: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13, color: '#8789a0' },
  btn: { width: '100%', padding: '15px 0', background: '#8BC63E', color: '#07080c', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 },
  empty: { textAlign: 'center', padding: 40, color: '#8a8da4' },

  // Step 3: Open Box
  bigIcon: { width: 80, height: 80, borderRadius: 20, background: 'rgba(77,144,254,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' },
  infoCard: { background: '#0f1117', border: '1px solid #252733', borderRadius: 12, padding: 16, margin: '20px 0' },
  infoRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #1a1c28' },
  infoLabel: { fontSize: 13, color: '#8789a0' },
  infoValue: { fontSize: 13, fontWeight: 600, color: '#e4e5ed' },
  openBtn: { width: '100%', padding: '14px 0', background: '#2f6fe0', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer', marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 },

  // Step 4: Waiting
  waitingCard: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, background: 'rgba(255,176,32,0.08)', border: '1px solid rgba(255,176,32,0.2)', borderRadius: 12, padding: '20px 24px', margin: '24px 0', fontSize: 15, fontWeight: 600, color: '#ffb020' },
  pulsingDot: { width: 12, height: 12, borderRadius: '50%', background: '#ffb020', animation: 'pulse 1.5s infinite' },
  confirmedCard: { background: 'rgba(139,198,62,0.08)', border: '1px solid rgba(139,198,62,0.2)', borderRadius: 12, padding: 16, margin: '20px 0' },
  confirmedRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', fontSize: 14, color: '#8BC63E', fontWeight: 500 },
};
